import { z } from 'zod';
import { articleUnitSchema } from '../inventory/article.schema';
import { cutLengthMmSchema } from '../inventory/cut-variant';
import { LAYOUT_ROW_MODES } from './layout-template-defaults';

/**
 * Projektauslegung (VB-26014, ADR 0081) — das Datenblatt, das Konstruktion und
 * Projektleitung bei der Projektübergabe ausfüllen. Fachlich ist es die Vorstufe
 * der Stückliste: sobald die Auslegung steht, sollen die Langläufer bestellt
 * werden können, lange vor der Planfreigabe.
 *
 * Eine Zeile ist in einem von drei Modi:
 * - `ARTICLE` — echter Artikel aus dem Stamm (Startmodus jeder Zeile). Wird zur
 *   Stücklisten-Position mit Artikelbezug.
 * - `COMMODITY_GROUP` — Platzhalter: nur Warengruppe + Bezeichnung, weil der
 *   Artikel noch nicht existiert (Hauptwelle, Skimrinne = Zuschnitte). Wird zur
 *   Stücklisten-Position OHNE Artikelbezug.
 * - `NOTE` — reine Angabe (Werkstoff, ITP, Sprache). Wird KEINE Position.
 *
 * Zeilen mit `projectField` (siehe `layout-template-defaults.ts`) werden hier
 * NICHT gespeichert: diese Werte stehen am Projekt (§6, eine Quelle der Wahrheit)
 * und werden über den Projekt-Endpunkt geschrieben. Das Blatt zeigt sie mit,
 * markiert sie aber als Projektdaten.
 */

export const layoutRowModeSchema = z.enum(LAYOUT_ROW_MODES);

/** Warengruppen-Code: 4-stellig wie im Zeichnungsnummern-Schema (ADR 0031). */
const commodityGroupCodeSchema = z.number().int().min(1000).max(9999);

export const layoutRowResponseSchema = z.object({
  id: z.string().uuid(),
  projectId: z.string().uuid(),
  /** Abschnitt des Datenblatts — wird bei der Stücklisten-Erzeugung Baugruppe. */
  section: z.string(),
  /** Reihenfolge innerhalb des Abschnitts. */
  position: z.number().int(),
  label: z.string(),
  /** Schlüssel der Vorlagenzeile; null = von Hand ergänzt. */
  templateKey: z.string().nullable(),
  mode: layoutRowModeSchema,
  /** Artikel-Modus: Soft-Ref auf den Stamm; null = Artikel noch nicht gewählt. */
  articleId: z.string().uuid().nullable(),
  articleNumber: z.string().nullable(),
  articleName: z.string().nullable(),
  /** Warengruppen-Modus: Code + Klartext der Warengruppe (Klartext vom Server aufgelöst). */
  commodityGroupCode: z.number().int().nullable(),
  commodityGroupName: z.string().nullable(),
  /** Bezeichnung des Platzhalters (wird die Bezeichnung der Stücklisten-Position). */
  placeholderLabel: z.string().nullable(),
  /**
   * Menge der Positionszeilen (Artikel + Platzhalter); null im Angabe-Modus.
   * Bei gesetzter `cutLengthMm` ist das die **Stückzahl**.
   */
  quantity: z.number().nullable(),
  unit: articleUnitSchema.nullable(),
  /**
   * Ablängung: Länge je Stück in mm (Positions-Parameter, ADR 0040 — dasselbe
   * Feld wie an einer Belegposition). „12 Stück á 5920 mm". Null = keine Ablängung.
   */
  cutLengthMm: z.number().int().nullable(),
  /** Angabe-Modus: der Wert, der im Excel in der Wertspalte stand. */
  noteValue: z.string().nullable(),
  createdAt: z.string().datetime(),
  updatedAt: z.string().datetime(),
});
export type LayoutRowResponse = z.infer<typeof layoutRowResponseSchema>;

/**
 * Zeile ergänzen — je Abschnitt, mit freier Bezeichnung. Sie startet im
 * Artikel-Modus ohne Artikel („Artikel wählen"), genau wie eine Vorlagenzeile.
 */
export const createLayoutRowSchema = z.object({
  section: z.string().trim().min(1, 'Abschnitt ist erforderlich.').max(120),
  label: z.string().trim().min(1, 'Bezeichnung ist erforderlich.').max(200),
});
export type CreateLayoutRowInput = z.infer<typeof createLayoutRowSchema>;

const labelShape = { label: z.string().trim().min(1, 'Bezeichnung ist erforderlich.').max(200) };
const noteShape = { noteValue: z.string().trim().max(2000).nullish() };

/**
 * Zeile innerhalb ihres Abschnitts um einen Platz verschieben. Wie bei den
 * Projektphasen zwei Pfeile statt Ziehen-und-Ablegen: Drag&Drop wäre eine neue
 * Top-Level-Abhängigkeit (§14) für einen Nutzen, den zwei Knöpfe auch bringen.
 */
export const moveLayoutRowSchema = z.object({ direction: z.enum(['up', 'down']) });
export type MoveLayoutRowInput = z.infer<typeof moveLayoutRowSchema>;
const quantityShape = {
  quantity: z.number().positive('Menge muss größer als 0 sein.'),
  unit: articleUnitSchema.nullish(),
  /**
   * Ablängung (ADR 0040): Länge je Stück in mm. Gesetzt heißt „Menge = Stückzahl".
   * Ganzzahlig und höchstens ein Kilometer — dieselbe Grenze wie an der
   * Belegposition (`cutLengthMmSchema`).
   */
  cutLengthMm: cutLengthMmSchema.nullish(),
};

/**
 * Zeile speichern. Der Modus entscheidet, was Pflicht ist — deshalb eine
 * unterscheidende Union statt eines Sammelobjekts mit lauter optionalen Feldern:
 * so kann keine Artikelzeile ohne Artikel und kein Platzhalter ohne Warengruppe
 * gespeichert werden. Beim Umschalten im Modal schickt das Frontend die ganze
 * Zeile; der Server räumt die Felder der anderen Modi auf.
 */
export const updateLayoutRowSchema = z.discriminatedUnion('mode', [
  z.object({
    mode: z.literal('ARTICLE'),
    ...labelShape,
    ...quantityShape,
    ...noteShape,
    /** Artikel aus dem Stamm — Pflicht, sonst gehört die Zeile in einen anderen Modus. */
    articleId: z.string().uuid('Bitte einen Artikel wählen.'),
  }),
  z.object({
    mode: z.literal('COMMODITY_GROUP'),
    ...labelShape,
    ...quantityShape,
    ...noteShape,
    commodityGroupCode: commodityGroupCodeSchema,
    /** Was beschafft werden soll — steht später als Bezeichnung in der Stückliste. */
    placeholderLabel: z.string().trim().min(1, 'Bezeichnung des Platzhalters ist erforderlich.').max(200),
  }),
  z.object({
    mode: z.literal('NOTE'),
    ...labelShape,
    ...noteShape,
  }),
]);
export type UpdateLayoutRowInput = z.infer<typeof updateLayoutRowSchema>;

/**
 * Ergebnis von „Vorlage laden": ergänzt fehlende Vorlagenzeilen und lässt
 * bestehende unangetastet (idempotent über `templateKey`) — zweimal laden legt
 * nichts doppelt an und überschreibt keine Eingabe.
 */
export const applyLayoutTemplateResultSchema = z.object({
  created: z.number().int().min(0),
  kept: z.number().int().min(0),
});
export type ApplyLayoutTemplateResult = z.infer<typeof applyLayoutTemplateResultSchema>;

/**
 * Stückliste aus der Auslegung erzeugen. Ziel ist entweder eine neue Bedarfsliste
 * (`name`) oder eine bestehende (`listId`, Positionen werden ersetzt) — genau
 * eines von beidem. Der Abschnitt der Zeile wird die Baugruppe der Position.
 */
export const generateLayoutMaterialListSchema = z
  .object({
    /** Bestehende Bedarfsliste, deren Positionen ersetzt werden. */
    listId: z.string().uuid().optional(),
    /** Bezeichnung einer neu anzulegenden Bedarfsliste. */
    name: z.string().trim().min(1).max(120).optional(),
    /**
     * Platzhalter (Warengruppen-Zeilen) mit übernehmen. Aus: nur Zeilen mit
     * echtem Artikel — sinnvoll, wenn die Liste direkt bestellt werden soll.
     */
    includePlaceholders: z.boolean().default(true),
    /**
     * Hinterlegtes Zusatzmaterial der gewählten Artikel mit übernehmen
     * (VB-26016, ADR 0088). Standard an — die Auslegung ist genau der Ort, an
     * dem die vollständige Stückliste entstehen soll.
     */
    includeKit: z.boolean().default(true),
  })
  .refine((input) => Boolean(input.listId) !== Boolean(input.name), {
    message: 'Entweder eine bestehende Bedarfsliste wählen oder eine neue benennen.',
  });
export type GenerateLayoutMaterialListInput = z.infer<typeof generateLayoutMaterialListSchema>;

export const generateLayoutMaterialListResultSchema = z.object({
  listId: z.string().uuid(),
  listName: z.string(),
  /** Angelegte Positionen mit Artikelbezug. */
  articles: z.number().int().min(0),
  /** Angelegte Platzhalter-Positionen (ohne Artikelbezug). */
  placeholders: z.number().int().min(0),
  /**
   * Zeilen, aus denen eine Position hätte werden sollen, aber keine wurde:
   * Artikelzeilen ohne gewählten Artikel — und Platzhalter, wenn sie
   * abgewählt wurden. Angaben zählen NICHT mit, die sind nie Positionen.
   */
  skipped: z.number().int().min(0),
  /** Zusätzlich angelegte Positionen aus dem Zusatzmaterial (ADR 0088). */
  kitLines: z.number().int().min(0),
  /** Ersetzte Positionen der Zielliste (0 bei neuer Liste). */
  replaced: z.number().int().min(0),
});
export type GenerateLayoutMaterialListResult = z.infer<typeof generateLayoutMaterialListResultSchema>;

/**
 * Artikelnummer einer Platzhalter-Position: die Warengruppe, gefolgt von einer
 * unbesetzten Artikelnummer. Liest sich im Haus-Schema `XXXX.YYYYY` (ADR 0031)
 * als „Warengruppe steht, Artikel fehlt noch" und sortiert bei der Warengruppe.
 */
export function placeholderArticleNumber(commodityGroupCode: number): string {
  return `${commodityGroupCode}.?????`;
}

/** Ist diese Bedarfsposition ein Platzhalter aus der Auslegung? */
export function isPlaceholderArticleNumber(articleNumber: string): boolean {
  return /^\d{4}\.\?{5}$/.test(articleNumber);
}
